import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Phone, Contact, KanbanSquare, Megaphone, PhoneIncoming, CalendarDays,
  Bot, Brain, Eye, BarChart3, CreditCard, Settings, LogOut, Menu, Hash, Radar, MessagesSquare, PhoneForwarded,
} from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { ts, P, F } from './api';
import { TSContext, ROLE_LABELS, Loading } from './ui';
import { AppCallProvider } from './AppCalls';

// `perm` hides the item from roles that can't use it, `feature` from plans without it;
// the server enforces the same rules.
const NAV = [
  { to: '/telesales/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/telesales/numbers', label: 'Numbers', icon: Hash, perm: P.NUMBERS },
  { to: '/telesales/control', label: 'Owner Control', icon: Radar, perm: P.SUPERVISE },
  { to: '/telesales/staff', label: 'Staff', icon: Users },
  { to: '/telesales/phone', label: 'Phone & SMS', icon: Phone },
  { to: '/telesales/leads', label: 'Leads', icon: Contact },
  { to: '/telesales/pipeline', label: 'Pipeline', icon: KanbanSquare },
  { to: '/telesales/campaigns', label: 'Campaigns', icon: Megaphone, feature: F.CAMPAIGNS },
  { to: '/telesales/callbacks', label: 'Callback Center', icon: PhoneIncoming },
  { to: '/telesales/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/telesales/front-desk', label: 'AI Front Desk', icon: Bot, perm: P.COMPANY, feature: F.AI_FRONT_DESK },
  { to: '/telesales/phone-menu', label: 'Phone Menu', icon: PhoneForwarded, perm: P.COMPANY },
  // Training the AI (website + files) is on every plan, like the main-number AI; asking it to work is a plan feature.
  { to: '/telesales/ai', label: 'AI Brain', icon: Brain, show: (can, has) => can(P.COMPANY) || (can(P.AI) && has(F.AI_BRAIN)) },
  { to: '/telesales/supervisor', label: 'Supervisor Center', icon: Eye, perm: P.SUPERVISE, feature: F.SUPERVISOR },
  { to: '/telesales/communications', label: 'Communications', icon: MessagesSquare, perm: P.COMMS },
  { to: '/telesales/analytics', label: 'Analytics', icon: BarChart3, perm: P.SUPERVISE, feature: F.ANALYTICS },
  { to: '/telesales/billing', label: 'Billing', icon: CreditCard, perm: P.BILLING },
  { to: '/telesales/settings', label: 'Settings', icon: Settings },
];

export default function TSLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [failed, setFailed] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setSession(await ts.me());
    } catch (e) {
      setFailed(e.message);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) { navigate('/login', { replace: true }); return; }
    refresh();
  }, [refresh, navigate]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (failed && /Aster Phone/.test(failed)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-3" data-testid="ts-use-app">
          <p className="text-foreground font-medium">Please use the Aster Phone app</p>
          <p className="text-sm text-muted-foreground">
            Your company's web portal is for the owner. Open Aster Phone on your phone, enter your company number and the one-time code your owner gives you.
          </p>
          <button className="text-sm text-primary" onClick={() => { localStorage.removeItem('auth_token'); navigate('/login'); }}>
            Sign in with another account
          </button>
        </div>
      </div>
    );
  }
  if (failed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-3">
          <p className="text-foreground font-medium">This login isn't part of a Tele Sale company.</p>
          <p className="text-sm text-muted-foreground">{failed}</p>
          <button className="text-sm text-primary" onClick={() => { localStorage.removeItem('auth_token'); navigate('/login'); }}>
            Sign in with another account
          </button>
        </div>
      </div>
    );
  }
  if (!session) return <div className="min-h-screen bg-background"><Loading /></div>;

  const perms = new Set(session.permissions);
  const can = (p) => perms.has(p);
  const features = new Set(session.features || []);
  const has = (f) => features.has(f);
  const status = session.company?.status;
  const inSetup = status === 'setup' || status === 'pending_payment';
  const onSetup = location.pathname.startsWith('/telesales/setup');
  if (inSetup && !onSetup) {
    if (can(P.BILLING)) return <Navigate to="/telesales/setup" replace />;
  }

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('vd_token');
    navigate('/login');
  };

  const items = NAV.filter(i => (!i.perm || can(i.perm)) && (!i.feature || has(i.feature)) && (!i.show || i.show(can, has)));
  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="px-4 h-14 flex items-center gap-2 border-b border-border shrink-0">
        <div className="w-7 h-7 rounded-md bg-violet-500/15 text-violet-500 flex items-center justify-center font-bold text-sm">TS</div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground truncate">{session.company?.legal_name || 'Tele Sale'}</div>
          {session.company?.main_number_e164 && (
            <div className="text-xs text-muted-foreground truncate" data-testid="ts-main-number">Main {fmtPhone(session.company.main_number_e164)}</div>
          )}
          <div className="text-[11px] text-muted-foreground truncate">{session.member?.full_name} · {ROLE_LABELS[session.role]} · <span data-testid="ts-plan-name">{session.pricing?.plan_name}</span></div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5" data-testid="ts-nav">
        {inSetup && can(P.BILLING) && (
          <NavLink to="/telesales/setup" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium bg-violet-500/15 text-violet-600 dark:text-violet-400">
            <Settings className="w-4 h-4" /> Finish setup
          </NavLink>
        )}
        {!inSetup && items.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`ts-nav-${to.split('/').pop()}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive ? 'bg-violet-500/15 text-violet-600 dark:text-violet-400 font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
            <Icon className="w-4 h-4 shrink-0" /> <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-2 shrink-0">
        <button onClick={logout} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-red-500 w-full" data-testid="ts-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <TSContext.Provider value={{ ...session, can, has, refresh }}>
      <AppCallProvider>
      <div className="flex h-screen bg-background text-foreground overflow-hidden" data-testid="ts-layout">
        <aside className="hidden lg:flex w-60 flex-col border-r border-border bg-card"><Sidebar /></aside>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
            <aside className="relative w-64 h-full bg-card border-r border-border"><Sidebar /></aside>
          </div>
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 border-b border-border flex items-center px-4 gap-3 shrink-0">
            <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(true)} aria-label="Open menu"><Menu className="w-5 h-5" /></button>
            <span className="text-xs text-muted-foreground">Tele Sale</span>
            <div className="flex-1" />
            <ThemeToggle compact />
          </header>
          {session.member?.must_change_password && session.role !== 'owner' && (
            <div className="bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm px-4 py-2 border-b border-amber-500/20">
              You're using a temporary password. <NavLink to="/telesales/settings" className="underline">Set your own password</NavLink>.
            </div>
          )}
          {inSetup && !can(P.BILLING) && (
            <div className="bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm px-4 py-2 border-b border-amber-500/20">
              Your company is still finishing setup. The owner needs to complete checkout before phones work.
            </div>
          )}
          <main className="flex-1 overflow-y-auto p-4 md:p-6"><Outlet /></main>
        </div>
      </div>
      </AppCallProvider>
    </TSContext.Provider>
  );
}

function fmtPhone(e164) {
  const d = (e164 || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}
